import React from "react";
import { Trophy, Users, Award, Crown, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { KpiTile, ChartCard, Skeleton, ErrorState, DataTable, RankList, PersonCell, ShareBar } from "../../analytics/primitives";
import { TrendChart, BarList } from "../../analytics/charts";
import { SERIES, STATUS } from "../../analytics/palette";

const STATUS_TONE = { active: "bg-emerald-50 text-emerald-700", draft: "bg-slate-100 text-slate-600", paused: "bg-amber-50 text-amber-700", completed: "bg-purple-50 text-purple-700", cancelled: "bg-rose-50 text-rose-700" };

const NominationsTab = ({ filters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useAnalyticsSection("nominations", filters);
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) return <Skeleton height={400} />;
  const d = q.data;
  const s = d.summary;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));
  const date = (v) => (v ? fmt.date(new Date(`${v}T00:00:00`), { month: "short", day: "numeric" }) : "—");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile icon={Trophy} label={t("nominations.total")} value={fmt.number(s.nominations.value)} metric={s.nominations} tone="yellow" sub={s.avg_per_campaign !== null ? t("nominations.per_campaign", { value: fmt.number(s.avg_per_campaign, { maximumFractionDigits: 1 }) }) : undefined} />
        <KpiTile icon={Users} label={t("nominations.nominators")} value={fmt.number(s.nominators.value)} metric={s.nominators} tone="blue" sub={t("kpi.rate_of_members", { rate: pctFmt(s.nominator_rate) })} />
        <KpiTile icon={Users} label={t("nominations.nominees")} value={fmt.number(s.nominees.value)} metric={s.nominees} tone="pink" sub={s.co_nominees ? t("nominations.co_nominees", { count: s.co_nominees }) : undefined} />
        <KpiTile icon={CalendarClock} label={t("nominations.campaigns")} value={fmt.number(s.campaigns_in_window)} tone="purple" sub={t("nominations.active_now", { count: s.campaigns_active })} />
        <KpiTile icon={Users} label={t("nominations.group_share")} value={pctFmt(s.group_share)} progress={s.group_share} tone="teal" sub={t("nominations.group_count", { count: s.group_nominations })} />
        <KpiTile icon={Award} label={t("nominations.points_awarded")} value={fmt.number(s.points_to_recipients)} tone="green" sub={s.points_to_senders ? t("nominations.points_to_senders", { value: fmt.number(s.points_to_senders) }) : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("nominations.trend_title")} icon={CalendarClock} className="lg:col-span-2" table={{ columns: [{ key: "bucket", label: t("table.period") }, { key: "nominations", label: t("nominations.total"), align: "right" }, { key: "nominators", label: t("nominations.nominators"), align: "right" }], rows: d.series, keyField: "bucket" }}>
          <TrendChart rows={d.series} granularity={d.scope?.granularity} series={[{ key: "nominations", label: t("nominations.total"), color: SERIES[4], kind: "bar" }, { key: "nominators", label: t("nominations.nominators"), color: SERIES[3], kind: "line" }]} />
        </ChartCard>
        <ChartCard title={t("nominations.by_award_title")} icon={Award}>
          <BarList data={d.by_award} shareKey="share" color={SERIES[4]} renderName={(a) => `${a.emoji && !String(a.emoji).startsWith("/") ? `${a.emoji} ` : ""}${a.name}`} emptyMessage={t("nominations.no_awards")} />
        </ChartCard>
      </div>

      <ChartCard title={t("nominations.campaigns_title")} subtitle={t("nominations.campaigns_subtitle")} icon={Trophy}>
        <DataTable
          keyField="id"
          rows={d.campaigns}
          emptyMessage={t("nominations.no_campaigns")}
          columns={[
            { key: "name", label: t("table.campaign"), render: (c) => (
              <div className="min-w-0">
                <Link to={`/admin/campaigns/${c.id}`} className="text-sm font-medium text-purple-700 hover:underline truncate block">{c.name}</Link>
                <p className="text-[11px] text-slate-500">{date(c.start_date)} – {date(c.end_date)}</p>
              </div>
            ) },
            { key: "status", label: t("table.status"), render: (c) => <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_TONE[c.status] || STATUS_TONE.draft}`}>{t(`campaign_status.${c.status}`, { defaultValue: c.status })}</span> },
            { key: "nominations", label: t("nominations.total"), align: "right" },
            { key: "nominators", label: t("nominations.nominators"), align: "right" },
            { key: "nominator_rate", label: t("nominations.reach"), render: (c) => <ShareBar value={c.nominator_rate || 0} color={(c.nominator_rate || 0) >= 30 ? STATUS.good : STATUS.warning} label={pctFmt(c.nominator_rate)} /> },
            { key: "nominees", label: t("nominations.nominees"), align: "right" },
            { key: "awards", label: t("nominations.awards"), align: "right", render: (c) => `${c.awards_decided}/${c.awards}` },
            { key: "winner", label: t("nominations.winner"), sortable: false, render: (c) => (c.winner ? <PersonCell user={c.winner} sub={c.winner_awarded_points ? `${fmt.number(c.winner_awarded_points)} ${t("points.pts")}` : undefined} /> : <span className="text-xs text-slate-400">—</span>) },
          ]}
        />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("nominations.top_nominees")} icon={Crown}>
          <RankList rows={d.top_nominees} valueLabel={t("nominations.total")} />
        </ChartCard>
        <ChartCard title={t("nominations.top_nominators")} icon={Users}>
          <RankList rows={d.top_nominators} valueLabel={t("nominations.total")} />
        </ChartCard>
      </div>
    </div>
  );
};

export default NominationsTab;
